import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SavedSearches = ({ className = '' }) => {
  const [savedSearches, setSavedSearches] = useState([
    {
      id: 1,
      name: "Liebherr Mobile Cranes 2018-2022",
      criteria: {
        make: "Liebherr",
        yearFrom: "2018",
        yearTo: "2022",
        region: "Northeast US"
      },
      alertsEnabled: true,
      lastRun: new Date(Date.now() - 86400000 * 2),
      resultCount: 23,
      newResults: 3
    },
    {
      id: 2,
      name: "Grove RT Cranes Under 5000 Hours",
      criteria: {
        make: "Grove",
        model: "RT Series",
        hoursTo: "5000",
        condition: "4+"
      },
      alertsEnabled: false,
      lastRun: new Date(Date.now() - 86400000 * 7),
      resultCount: 15,
      newResults: 0
    },
    {
      id: 3,
      name: "High Capacity Cranes 200t+",
      criteria: {
        capacity: "200+",
        region: "All US",
        condition: "3+"
      },
      alertsEnabled: true,
      lastRun: new Date(Date.now() - 86400000 * 1),
      resultCount: 8,
      newResults: 1
    }
  ]);

  const [isExpanded, setIsExpanded] = useState(false);

  const formatLastRun = (date) => {
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date?.toLocaleDateString();
  };

  const handleRunSearch = (search) => {
    console.log('Running saved search:', search);
    // In real app, this would apply the saved criteria and run the search
  };

  const handleToggleAlert = (searchId) => {
    setSavedSearches(prev => 
      prev?.map(search => 
        search?.id === searchId 
          ? { ...search, alertsEnabled: !search?.alertsEnabled }
          : search
      )
    );
  };

  const handleDeleteSearch = (searchId) => {
    setSavedSearches(prev => prev?.filter(search => search?.id !== searchId));
  };

  const handleCreateNew = () => {
    console.log('Create new saved search');
    // In real app, this would open a modal to save current search criteria
  };

  if (savedSearches?.length === 0) {
    return (
      <div className={`bg-surface border border-border rounded-lg shadow-card p-6 ${className}`}>
        <div className="text-center">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Bookmark" size={20} className="text-text-secondary" />
          </div>
          <h3 className="font-medium text-text-primary mb-2">No Saved Searches</h3>
          <p className="text-sm text-text-secondary mb-4">
            Save your search criteria to quickly find similar cranes in the future
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCreateNew}
            iconName="Plus"
            iconPosition="left"
          >
            Save Current Search
          </Button>
        </div>
      </div>
    );
  }

  const displayedSearches = isExpanded ? savedSearches : savedSearches?.slice(0, 2);

  return (
    <div className={`bg-surface border border-border rounded-lg shadow-card ${className}`}>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
              <Icon name="Bookmark" size={16} color="white" />
            </div>
            <div>
              <h3 className="font-medium text-text-primary">Saved Searches</h3>
              <p className="text-sm text-text-secondary">Quick access to your frequent searches</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCreateNew}
            iconName="Plus"
          />
        </div>
      </div>
      <div className="p-4 space-y-3">
        {displayedSearches?.map((search) => (
          <div key={search?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-medium text-text-primary">{search?.name}</h4>
                  {search?.newResults > 0 && (
                    <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs rounded-full">
                      {search?.newResults} new
                    </span>
                  )}
                </div>
                <p className="text-sm text-text-secondary">
                  Last run: {formatLastRun(search?.lastRun)} • {search?.resultCount} results
                </p>
              </div>
              
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleToggleAlert(search?.id)}
                  iconName={search?.alertsEnabled ? "Bell" : "BellOff"}
                  className={search?.alertsEnabled ? "text-accent" : "text-text-secondary"}
                />
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleDeleteSearch(search?.id)}
                  iconName="Trash2"
                  className="text-text-secondary hover:text-destructive"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(search?.criteria)?.map(([key, value]) => (
                <span key={key} className="px-2 py-1 bg-muted text-text-secondary text-xs rounded">
                  {key}: {value}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={search?.alertsEnabled ? "Bell" : "BellOff"} 
                  size={12} 
                  className={search?.alertsEnabled ? "text-accent" : "text-text-secondary"} 
                />
                <span className="text-xs text-text-secondary">
                  Alerts {search?.alertsEnabled ? 'enabled' : 'disabled'}
                </span>
              </div>
              
              <Button
                variant="outline"
                size="xs"
                onClick={() => handleRunSearch(search)}
                iconName="Play"
                iconPosition="left"
              >
                Run Search
              </Button>
            </div>
          </div>
        ))}

        {savedSearches?.length > 2 && (
          <div className="text-center pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
            >
              {isExpanded ? 'Show Less' : `Show ${savedSearches?.length - 2} More`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedSearches;